
import { defineComponent } from 'vue'

import AuthMixin from '~/mixins/authMixin'
import InstallAutofixMixin from '~/mixins/installAutofixMixin'
import PortalMixin from '~/mixins/portalMixin'
import RepoDetailMixin from '~/mixins/repoDetailMixin'
import RoleAccessMixin from '~/mixins/roleAccessMixin'
import { CONFIG_MAP_ANALYZER_TYPE } from '~/mixins/tomlGeneratorMixin'
import { RepoConfigInterface } from '~/store/repository/detail'

import { AnalyzerAnalyzerType, RepositoryKindChoices, RunStatus } from '~/types/types'

/**
 * Layout file for `repository` views.
 */
export default defineComponent({
  name: 'RepositoryLayout',
  mixins: [AuthMixin, InstallAutofixMixin, RepoDetailMixin, PortalMixin, RoleAccessMixin],
  middleware: ['hidePrivateRepo', 'protectMonorepoRoutes'],
  head: {
    bodyAttrs: {
      class: 'antialiased stroke-2 hide-scroll'
    }
  },
  data() {
    return {
      pollingId: null as ReturnType<typeof setTimeout> | null,
      activateAnalysisLoading: false
    }
  },
  async fetch() {
    try {
      await this.fetchBasicRepoDetails(this.baseRouteParams)
    } catch (e) {
      this.$logErrorAndToast(e as Error, 'There was a problem loading this repository.')
    }
  },
  mounted() {
    this.$socket.$on('repo-analysis-updated', this.refetchData)
    this.$socket.$on('repo-config-committed', this.refetchData)
    this.$socket.$on('repo-config-updated', this.refetchData)
    this.$socket.$on('autofix-installation-complete', this.refetchData)
  },
  beforeDestroy() {
    this.$socket.$off('repo-analysis-updated', this.refetchData)
    this.$socket.$off('repo-config-committed', this.refetchData)
    this.$socket.$off('repo-config-updated', this.refetchData)
    this.$socket.$off('autofix-installation-complete', this.refetchData)
    if (this.pollingId) {
      clearTimeout(this.pollingId)
    }
  },
  computed: {
    isAnalyzed(): boolean {
      return Boolean(this.repository?.latestAnalysisRun?.id)
    },
    repoHasCode(): boolean {
      return Boolean(this.repository.defaultBranchName)
    },
    hasLastRunTimedOut(): boolean {
      return this.repository?.lastRun?.status === RunStatus.Timo
    },
    allowedOnBroken(): boolean {
      if (this.repository.kind === RepositoryKindChoices.Monorepo) {
        return true
      }

      if (this.repository.errorCode === 3003) {
        // For ad-hoc runs
        return true
      }

      const allowedRoutes = [
        'provider-owner-repo-settings',
        'provider-owner-repo-generate-config',
        'provider-owner-repo-history',
        'provider-owner-repo-transform',
        'provider-owner-repo-run',
        'provider-owner-repo-dependencies'
      ]
      const matchResults = allowedRoutes.map((name) => {
        if (this.$route.name) {
          return this.$route.name.startsWith(name)
        }
        return false
      })

      return matchResults.indexOf(true) > -1 ? true : false
    },
    isRepoWaiting(): boolean {
      return (
        !this.isAnalyzed &&
        !this.allowedOnBroken &&
        this.$fetchState &&
        !this.$fetchState.pending &&
        !this.repository.errorCode &&
        this.repoHasCode &&
        !this.hasLastRunTimedOut
      )
    },
    waitingOnCommunityAnalyzers() {
      if (this.isRepoWaiting) {
        return (this.repository.config as RepoConfigInterface)?.analyzers?.every(
          (analyzerConfig) =>
            CONFIG_MAP_ANALYZER_TYPE[analyzerConfig.type] === AnalyzerAnalyzerType.Community
        )
      }
      return false
    }
  },
  methods: {
    triggerPoll(newIsRepoWaiting: boolean): void {
      if (this.pollingId) {
        clearTimeout(this.pollingId)
      }
      if (newIsRepoWaiting) {
        this.pollRepo()
      }
    },
    pollRepo(): void {
      const POLLING_INTERVAL = 5000
      if (process.client) {
        this.pollingId = setTimeout(async () => {
          await this.pollRepoStatus(this.baseRouteParams)
          if (this.isRepoWaiting) {
            this.pollRepo()
          }
        }, POLLING_INTERVAL)
      }
    },
    async activateAnalysis({ showSuccessToast = false } = {}): Promise<void> {
      this.activateAnalysisLoading = true
      await this.toggleRepoActivation({
        isActivated: true,
        id: this.repository.id,
        login: this.$route.params.owner,
        provider: this.$route.params.provider
      })
      this.activateAnalysisLoading = false

      if (showSuccessToast) {
        this.$toast.success(
          `Triggered run for ${this.repository.displayName}, this might take a while.`
        )
      }

      this.refetchData()
    },
    async refetchData(): Promise<void> {
      const args = {
        ...this.baseRouteParams,
        refetch: true
      }

      await Promise.all([this.fetchBasicRepoDetails(args), this.fetchWidgets(args)])
    }
  },
  watch: {
    isRepoWaiting: {
      handler(newIsRepoWaiting: boolean) {
        this.triggerPoll(newIsRepoWaiting)
      },
      immediate: true
    }
  }
})
